<template>
  <div class="home-container">
    <!-- 3D Background -->
    <div id="canvas-container" class="fixed inset-0 z-0 pointer-events-none"></div>

    <!-- Navigation -->
    <nav v-if="$route.path === '/'" id="navbar" class="fixed w-full z-50 transition-all duration-300">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center h-20">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
              <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
            <span class="text-2xl font-bold tracking-tight">
              Simu<span class="gradient-text">Trade</span>
            </span>
          </div>

          <div class="hidden md:flex items-center gap-8">
            <a href="#story" class="nav-link text-sm font-medium text-slate-300 hover:text-white transition-colors">我的故事</a>
            <router-link to="/strategy" class="nav-link text-sm font-medium text-slate-300 hover:text-white transition-colors">功能</router-link>
            <router-link to="/strategy" class="nav-link text-sm font-medium text-slate-300 hover:text-white transition-colors">交易终端</router-link>
            <a href="#donate" class="nav-link text-sm font-medium text-amber-400 hover:text-amber-300 transition-colors">支持我</a>
            <button @click="goToStrategy"
              class="btn-shine px-6 py-2.5 rounded-full bg-gradient-to-r from-primary to-secondary text-white font-semibold text-sm hover:shadow-lg hover:shadow-primary/25 transition-all transform hover:scale-105 cursor-pointer">
              开始模拟交易
            </button>
          </div>

          <button id="mobile-menu-btn" class="md:hidden text-white" @click="mobileMenuOpen = !mobileMenuOpen">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </div>
    </nav>

    <!-- Mobile Menu -->
    <div v-if="$route.path === '/'" :class="['mobile-menu', { 'open': mobileMenuOpen }]" class="fixed inset-0 z-40 bg-darker/95 backdrop-blur-xl md:hidden">
      <div class="flex flex-col items-center justify-center h-full gap-8">
        <a href="#story" class="text-2xl font-medium text-white mobile-link" @click="mobileMenuOpen = false">我的故事</a>
        <router-link to="/strategy" class="text-2xl font-medium text-white" @click="mobileMenuOpen = false">功能</router-link>
        <router-link to="/strategy" class="text-2xl font-medium text-white" @click="mobileMenuOpen = false">交易终端</router-link>
        <a href="#donate" class="text-2xl font-medium text-amber-400" @click="mobileMenuOpen = false">支持我</a>
        <button @click="goToStrategy(); mobileMenuOpen = false" class="mt-4 px-8 py-3 rounded-full bg-gradient-to-r from-primary to-secondary text-white font-semibold cursor-pointer">
          开始模拟交易
        </button>
      </div>
    </div>

    <!-- Hero Section -->
    <section v-if="$route.path === '/'" class="relative min-h-screen flex items-center justify-center pt-20 overflow-hidden fullpage-section">
      <div class="particles" id="particles"></div>

      <div class="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div class="inline-flex items-center gap-2 px-4 py-2 rounded-full glass mb-8 float-1">
          <span class="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
          <span class="text-sm font-medium text-slate-300">完全免费 · 开源精神 · 社区共建</span>
        </div>

        <h1 class="text-5xl md:text-7xl lg:text-8xl font-black mb-6 leading-tight">
          <span class="block text-white">写代码的转行炒期货</span>
          <span class="block gradient-text mt-2">顺手写了个模拟器</span>
        </h1>

        <p class="text-xl md:text-2xl text-slate-400 max-w-3xl mx-auto mb-12 leading-relaxed">
          10年程序员，All in 期货后发现市面上除了 TradingView 根本没有好用的中文期货模拟软件。<br>
          <span class="text-white font-semibold">既然找不到，那就自己造一个。免费给大家用。</span>
        </p>

        <div class="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
          <button @click="goToStrategy"
            class="btn-shine group px-8 py-4 rounded-full bg-gradient-to-r from-primary to-secondary text-white font-bold text-lg hover:shadow-2xl hover:shadow-primary/30 transition-all transform hover:scale-105 flex items-center gap-2 cursor-pointer">
            免费开始模拟交易
            <svg class="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </button>
          <a href="#story"
            class="px-8 py-4 rounded-full glass text-white font-semibold text-lg hover:bg-white/10 transition-all flex items-center gap-2 no-underline">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            了解背后的故事
          </a>
        </div>

        <!-- Live Market Ticker -->
        <div class="glass rounded-2xl p-6 max-w-5xl mx-auto">
          <div class="ticker-wrap">
            <div class="ticker flex gap-8 text-sm">
              <div class="inline-flex items-center gap-3 px-4">
                <span class="font-bold text-white">焦煤</span>
                <span class="price-up">3456.00</span>
                <span class="price-up text-xs">↑ 1.23%</span>
              </div>
              <div class="inline-flex items-center gap-3 px-4">
                <span class="font-bold text-white">螺纹钢</span>
                <span class="price-down">4125.00</span>
                <span class="price-down text-xs">↓ 0.45%</span>
              </div>
              <div class="inline-flex items-center gap-3 px-4">
                <span class="font-bold text-white">铁矿石</span>
                <span class="price-up">892.50</span>
                <span class="price-up text-xs">↑ 2.10%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <svg class="w-6 h-6 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
        </svg>
      </div>
    </section>

    <!-- Story Section -->
    <section v-if="$route.path === '/'" id="story" class="relative py-32 grid-bg fullpage-section">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid lg:grid-cols-2 gap-16 items-center">
          <div class="reveal">
            <div class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              关于开发者
            </div>
            <h2 class="text-4xl md:text-5xl font-bold mb-8">
              从 <span class="text-primary">Ctrl+C/V</span> 到 <span class="gradient-text">Buy/Sell</span>
            </h2>

            <div class="space-y-6 text-lg text-slate-300 leading-relaxed">
              <p>
                写代码写了10年，从 CRUD 到微服务，从 Vue 到 Kubernetes，什么技术栈都玩过。后来觉得打工没意思，All in 了期货。
              </p>
              <p>
                结果发现这行水太深——交了不知道多少学费，到现在也<span class="text-amber-400 font-semibold">还没实现稳定盈利</span>。但既然选择了这条路，就得认真走下去。
              </p>
              <p>
                想找个靠谱的模拟软件练手，翻遍了全网，除了 TradingView（英文界面+期货数据不全），国内居然没有一个像样的期货模拟平台。要么是收费死贵，要么是功能残废。
              </p>
              <p class="text-white font-semibold text-xl">
                "既然我是程序员，为什么不自己写一个？"
              </p>
              <p>
                于是 SimuTrade 诞生了。白天看盘，晚上写代码，周末修 bug。不为别的，就为了自己用着顺手，也希望帮到和我一样在探索中的交易者。
              </p>
            </div>

            <div class="mt-8 flex items-center gap-4">
              <div class="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white font-bold text-lg">
                程
              </div>
              <div>
                <div class="text-white font-semibold">程序员转行的期货韭菜</div>
                <div class="text-slate-400 text-sm">仍在探索稳定盈利的路上</div>
              </div>
            </div>
          </div>

          <div class="reveal">
            <div class="glass-card rounded-2xl p-8 space-y-6">
              <h3 class="text-2xl font-bold text-white mb-4">我的现状</h3>

              <div class="space-y-4">
                <div class="flex items-center justify-between p-4 rounded-xl bg-white/5">
                  <div class="flex items-center gap-3">
                    <div class="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                      <svg class="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <span class="text-slate-300">编程能力</span>
                  </div>
                  <span class="text-emerald-400 font-bold">10年经验 ✓</span>
                </div>

                <div class="flex items-center justify-between p-4 rounded-xl bg-white/5">
                  <div class="flex items-center gap-3">
                    <div class="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                      <svg class="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <span class="text-slate-300">交易经验</span>
                  </div>
                  <span class="text-amber-400 font-bold">持续学习中...</span>
                </div>

                <div class="flex items-center justify-between p-4 rounded-xl bg-white/5">
                  <div class="flex items-center gap-3">
                    <div class="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                      <svg class="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <span class="text-slate-300">稳定盈利</span>
                  </div>
                  <span class="text-red-400 font-bold">尚未达成 ✗</span>
                </div>

                <div class="flex items-center justify-between p-4 rounded-xl bg-white/5">
                  <div class="flex items-center gap-3">
                    <div class="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                      <svg class="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                      </svg>
                    </div>
                    <span class="text-slate-300">开发热情</span>
                  </div>
                  <span class="text-primary font-bold">满格 ⚡</span>
                </div>
              </div>

              <div class="mt-6 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
                <p class="text-amber-300 text-sm leading-relaxed">
                  <span class="font-bold">坦白说：</span>我自己还在交学费的阶段，所以这个工具也是我和大家一起成长的见证。如果你已经稳定盈利了，欢迎来指点一下我 🙏
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Markets Section -->
    <section v-if="$route.path === '/'" id="markets" class="relative py-32 grid-bg fullpage-section">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-16 reveal">
          <h2 class="text-4xl md:text-5xl font-bold mb-6">
            已支持的<span class="gradient-text">期货品种</span>
          </h2>
          <p class="text-xl text-slate-400">持续增加中，有需求就告诉我</p>
        </div>

        <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 reveal">
          <div class="glass rounded-xl p-4 text-center hover:bg-white/10 transition-all hover:scale-105 cursor-pointer border border-white/10">
            <div class="w-12 h-12 mx-auto mb-3 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
              <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <span class="text-white font-medium">焦煤主力</span>
          </div>
          <div class="glass rounded-xl p-4 text-center hover:bg-white/10 transition-all hover:scale-105 cursor-pointer border border-white/10">
            <div class="w-12 h-12 mx-auto mb-3 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
              <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <span class="text-white font-medium">螺纹钢</span>
          </div>
          <div class="glass rounded-xl p-4 text-center hover:bg-white/10 transition-all hover:scale-105 cursor-pointer border border-white/10">
            <div class="w-12 h-12 mx-auto mb-3 rounded-full bg-gradient-to-br from-red-500 to-pink-500 flex items-center justify-center">
              <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <span class="text-white font-medium">铁矿石</span>
          </div>
          <div class="glass rounded-xl p-4 text-center hover:bg-white/10 transition-all hover:scale-105 cursor-pointer border border-white/10">
            <div class="w-12 h-12 mx-auto mb-3 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
              <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <span class="text-white font-medium">豆粕</span>
          </div>
          <div class="glass rounded-xl p-4 text-center hover:bg-white/10 transition-all hover:scale-105 cursor-pointer border border-white/10">
            <div class="w-12 h-12 mx-auto mb-3 rounded-full bg-gradient-to-br from-yellow-500 to-amber-500 flex items-center justify-center">
              <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <span class="text-white font-medium">原油</span>
          </div>
          <div class="glass rounded-xl p-4 text-center hover:bg-white/10 transition-all hover:scale-105 cursor-pointer border border-white/10">
            <div class="w-12 h-12 mx-auto mb-3 rounded-full bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center">
              <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <span class="text-white font-medium">黄金</span>
          </div>
        </div>

        <div class="mt-8 text-center">
          <p class="text-slate-400 mb-4">需要其他品种？发邮件告诉我，优先安排</p>
          <a href="mailto:simutrade@example.com"
            class="inline-flex items-center gap-2 text-primary hover:text-primary/80 transition-colors">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            simutrade@example.com
          </a>
        </div>
      </div>
    </section>

    <!-- Personal Vision Section -->
    <section v-if="$route.path === '/'" class="relative py-20 overflow-hidden fullpage-section">
      <div class="absolute inset-0 bg-gradient-to-r from-violet-500/10 to-indigo-500/10"></div>
      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div class="bg-violet-500/10 rounded-2xl p-8 border border-violet-500/20 reveal">
          <h3 class="text-violet-300 font-bold text-xl mb-4">✨ 个人美好愿景</h3>
          <p class="text-slate-300 leading-relaxed text-lg">
            国内目前各类交易软件实在差强人意，市面上没有任何厂商打磨出一款能够对标TradingView的专业行情工具。不少上市软件企业市值走高、创始人动辄数十亿套现离场，却不愿投入资源打造一款贴合国内交易者的优质软件，每每想到此处都颇为惋惜。
            <br><br>
            我本科主修英语专业，之后自学编程，深耕全栈开发整整十年，熟练掌握UI交互、前后端整套开发。在此立下心愿：未来潜心打造一款本土化、专为国内交易者定制的国产版TradingView，告别原版每月近200美元高额订阅费、行情延迟15分钟、标的品种残缺不全的困境。
            <br><br>
            当然现阶段还只是长远理想。我现阶段实盘亏损已经可控，但尚未实现稳定盈利，不过自身交易体系持续完善，距离稳定盈利已然不远，待到交易稳步盈利后，便一步步落地这款自研软件的梦想。
          </p>
        </div>
      </div>
    </section>

    <!-- Donate Section -->
    <section v-if="$route.path === '/'" id="donate" class="relative py-32 overflow-hidden fullpage-section">
      <div class="absolute inset-0 bg-gradient-to-r from-amber-500/10 to-orange-500/10"></div>

      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div class="text-center mb-12 reveal">
          <div class="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 text-amber-400 text-sm font-medium mb-6">
            <svg class="w-4 h-4 heart-beat" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
            </svg>
            服务器快撑不住了
          </div>
          <h2 class="text-4xl md:text-5xl font-bold mb-6">
            请我喝杯咖啡？<br>
            <span class="gradient-text-gold">或者赞助台服务器</span>
          </h2>
        </div>

        <div class="grid lg:grid-cols-2 gap-16 items-stretch">
          <div class="reveal h-full">
            <div class="glass-card rounded-2xl p-8 space-y-6 h-full flex flex-col">
              <h3 class="text-2xl font-bold text-white mb-4">项目说明</h3>

              <div class="space-y-6 text-lg text-slate-300 leading-relaxed flex-1">
                <p>
                  先说清楚：<span class="text-white font-bold">这个网站完全免费，以后也永久免费。</span> 无付费套餐、无VIP、无广告、无强制收费。
                </p>

                <p>
                  服务器、带宽、行情数据接口每月都有成本，目前全部由我个人承担。我自己仍在交易学习阶段，经济能力有限，因此开放自愿赞助渠道。
                </p>

                <div class="bg-amber-500/10 rounded-xl p-6 border border-amber-500/20">
                  <p class="text-slate-200 leading-relaxed">
                    <span class="text-amber-400 font-bold">数据采购说明：</span>
                    平台所需的期货行情数据需要自费向数据服务商采购，单个品种数据年费<span class="text-amber-300 font-bold">250元</span>。我没有官方免费数据源，全部自掏腰包外购。
                    <br><br>
                    我日常主攻焦煤交易，因此<span class="text-emerald-400 font-bold">焦煤品种永久免费开放使用</span>。待日后交易稳定盈利、资金充裕后，我会大批量采购全市场数据，彻底全品种免费来回馈所有使用者，还望各位理解包容。
                  </p>
                </div>

                <p>
                  如果你觉得工具对你有帮助，可自愿小额赞助，所有资金仅用于维持服务器运行与项目更新、扩充采购更多品种行情数据。
                </p>

                <div class="text-center py-4">
                  <p class="text-white font-semibold text-xl">赞助完全自愿，不赞助不影响任何使用权限</p>
                  <p class="text-slate-400 mt-2">一块不嫌少，一百不嫌多。让这个免费项目能一直活下去。</p>
                </div>
              </div>
            </div>
          </div>

          <div class="reveal h-full">
            <div class="glass-card rounded-2xl p-8 space-y-6 h-full flex flex-col">
              <h3 class="text-2xl font-bold text-white mb-4">选择赞助方式</h3>

              <div class="space-y-4">
                <button @click="showQrcodeModal = true"
                  class="w-full group rounded-xl p-4 bg-white/5 hover:bg-white/10 transition-all flex items-center gap-4 cursor-pointer">
                  <div class="w-14 h-14 rounded-xl bg-blue-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <svg class="w-7 h-7 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <div class="text-left flex-1">
                    <div class="text-white font-semibold">支付宝赞助</div>
                    <div class="text-slate-400 text-sm">点击查看收款码</div>
                  </div>
                  <svg class="w-5 h-5 text-slate-500 group-hover:text-blue-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                  </svg>
                </button>

                <button @click="showQrcodeModal = true"
                  class="w-full group rounded-xl p-4 bg-white/5 hover:bg-white/10 transition-all flex items-center gap-4 cursor-pointer">
                  <div class="w-14 h-14 rounded-xl bg-emerald-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <svg class="w-7 h-7 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                    </svg>
                  </div>
                  <div class="text-left flex-1">
                    <div class="text-white font-semibold">微信赞助</div>
                    <div class="text-slate-400 text-sm">点击查看收款码</div>
                  </div>
                  <svg class="w-5 h-5 text-slate-500 group-hover:text-emerald-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>

              <div class="bg-stone-500/10 rounded-xl p-6 border border-stone-500/20">
                <p class="text-slate-300 leading-relaxed">
                  <span class="text-stone-300 font-bold">心语说明：</span>
                  若有朋友对自愿捐赠的形式存有不同看法，可自由选择其他同类平台。本人日常潜心修佛、静心参禅，开发工具之余专心摸索交易，只求一份清净、稳步探寻稳定盈利之路。倘若借捐赠事宜无端非议、刻意骚扰诋毁，便不再与之争辩往来，还请彼此尊重、各安其所。
                </p>
              </div>

              <div class="bg-sky-500/10 rounded-xl p-6 border border-sky-500/20">
                <p class="text-slate-300 leading-relaxed text-sm">
                  <span class="text-sky-300 font-bold">捐助温馨提示：</span>
                  捐助前还请审慎抉择，如同期货开仓切勿随性而为。交易开仓失误尚可止损离场，但自愿捐赠款项一经收款到账，一概不予退还。
                </p>
              </div>

              <div class="p-4 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-slate-400 leading-relaxed">
                <strong class="text-red-400">自愿赞助免责声明：</strong><br>
                1. 本赞助为用户对免费软件的自愿赠与行为，不属于商品买卖、服务购买，不构成合同关系；<br>
                2. 赞助后不予退款，因个人误操作、误转款产生的损失由付款人自行承担；<br>
                3. 本项目不属于慈善募捐、公益捐赠，不提供公益捐赠票据与抵税凭证。
              </div>

              <div class="text-center pt-4 mt-auto">
                <div class="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 text-slate-400 text-sm">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  累计收到赞助 <span class="text-amber-400 font-bold">¥0</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Contact Section -->
    <section v-if="$route.path === '/'" class="relative py-32 grid-bg fullpage-section">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-16 reveal">
          <h2 class="text-4xl md:text-5xl font-bold mb-6">
            联系<span class="gradient-text">开发者</span>
          </h2>
          <p class="text-xl text-slate-400">有问题直接找我，别客气</p>
        </div>

        <div class="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
          <div class="glass-card rounded-2xl p-8 text-center reveal">
            <div class="w-14 h-14 mx-auto rounded-xl bg-primary/20 flex items-center justify-center mb-6">
              <svg class="w-7 h-7 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 class="text-lg font-bold text-white mb-2">邮件联系</h3>
            <p class="text-slate-400 text-sm mb-4">Bug 反馈、品种需求、交易心得交流</p>
            <a href="mailto:simutrade@example.com"
              class="text-primary hover:text-primary/80 transition-colors font-mono text-sm">
              simutrade@example.com
            </a>
          </div>

          <div class="glass-card rounded-2xl p-8 text-center reveal">
            <div class="w-14 h-14 mx-auto rounded-xl bg-emerald-500/20 flex items-center justify-center mb-6">
              <svg class="w-7 h-7 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
            </div>
            <h3 class="text-lg font-bold text-white mb-2">意见提交</h3>
            <p class="text-slate-400 text-sm mb-4">直接在交易页面提交，我会定期查看</p>
            <span class="text-slate-500 text-sm">页面右下角 "意见反馈" 按钮</span>
          </div>

          <div class="glass-card rounded-2xl p-8 text-center reveal">
            <div class="w-14 h-14 mx-auto rounded-xl bg-amber-500/20 flex items-center justify-center mb-6">
              <svg class="w-7 h-7 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3 class="text-lg font-bold text-white mb-2">交易交流</h3>
            <p class="text-slate-400 text-sm mb-4">欢迎大佬带带我，也欢迎一起探讨</p>
            <span class="text-slate-500 text-sm">邮件备注 "交流" 即可</span>
          </div>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer v-if="$route.path === '/'" class="relative border-t border-white/5 pt-20 pb-10">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid md:grid-cols-3 gap-12 mb-16">
          <div>
            <div class="flex items-center gap-3 mb-6">
              <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <span class="text-2xl font-bold">Simu<span class="gradient-text">Trade</span></span>
            </div>
            <p class="text-slate-400 leading-relaxed mb-4">
              一个程序员转行期货后的自救项目。免费、开源精神、社区共建。
            </p>
            <p class="text-amber-400/80 text-sm">
              服务器由捐赠支持，感谢每一位贡献者。
            </p>
          </div>

          <div>
            <h4 class="text-white font-semibold mb-6">快速链接</h4>
            <ul class="space-y-3 text-slate-400">
              <li><button @click="goToStrategy" class="hover:text-primary transition-colors cursor-pointer">开始交易</button></li>
              <li><router-link to="/strategy" class="hover:text-primary transition-colors">功能介绍</router-link></li>
              <li><a href="#donate" class="hover:text-amber-400 transition-colors">支持项目</a></li>
            </ul>
          </div>

          <div>
            <h4 class="text-white font-semibold mb-6">免责声明</h4>
            <p class="text-slate-500 text-sm leading-relaxed">
              本模拟平台仅供学习交流，不构成任何投资建议。期货交易风险极高，可能导致本金全部损失。请理性投资，量力而行。作者不对任何交易结果负责。
            </p>
          </div>
        </div>

        <div class="border-t border-white/5 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p class="text-slate-500 text-sm">© 2025 SimuTrade. 一个还在交学费的开发者制作。</p>
          <div class="flex gap-6 text-sm text-slate-500">
            <span>Made with 💙 and ☕ by 一个程序员</span>
          </div>
        </div>
      </div>
    </footer>

    <!-- QR Code Modal -->
    <div v-if="showQrcodeModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-md" @click.self="showQrcodeModal = false">
      <div class="relative bg-[#121826] rounded-2xl p-8 w-full max-w-lg mx-4 shadow-2xl border border-white/10">
        <button @click="showQrcodeModal = false"
          class="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-white/70 hover:text-white text-xl cursor-pointer">
          ✕
        </button>
        <h3 class="text-xl font-bold text-white mb-6 text-center">自愿赞助 · 支付宝 / 微信</h3>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div class="text-center">
            <div class="bg-white rounded-xl p-2 inline-block">
              <div class="w-52 h-52 bg-gray-200 flex items-center justify-center text-gray-500">支付宝收款码</div>
            </div>
            <p class="mt-3 text-slate-300">支付宝扫码</p>
          </div>
          <div class="text-center">
            <div class="bg-white rounded-xl p-2 inline-block">
              <div class="w-52 h-52 bg-gray-200 flex items-center justify-center text-gray-500">微信收款码</div>
            </div>
            <p class="mt-3 text-slate-300">微信扫码</p>
          </div>
        </div>
        <p class="mt-6 text-slate-400 text-sm text-center">自愿随心赞助，金额不限，不赞助不影响软件任何功能</p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Home',
  data() {
    return {
      mobileMenuOpen: false,
      showQrcodeModal: false,
      particles: [],
      currentSection: 0,
      isScrolling: false
    }
  },
  mounted() {
    this.initParticles()
    this.initScrollReveal()
    this.initNavbar()
    this.initFullPageScroll()
  },
  beforeDestroy() {
    window.removeEventListener('wheel', this.handleWheel)
  },
  methods: {
    goToStrategy() {
      console.log('Navigating to strategy page...')
      this.$router.replace({ path: '/strategy' }).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          console.error('Navigation error:', err)
        } else {
          console.log('Already on strategy page')
        }
      })
    },
    initParticles() {
      const container = document.getElementById('particles')
      if (!container) return
      
      for (let i = 0; i < 30; i++) {
        const particle = document.createElement('div')
        particle.className = 'particle'
        particle.style.left = Math.random() * 100 + '%'
        particle.style.animationDelay = Math.random() * 15 + 's'
        particle.style.animationDuration = (10 + Math.random() * 10) + 's'
        container.appendChild(particle)
      }
    },
    initScrollReveal() {
      const reveals = document.querySelectorAll('.reveal')
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active')
          }
        })
      }, { threshold: 0.1 })
      
      reveals.forEach(el => observer.observe(el))
    },
    initNavbar() {
      const navbar = document.getElementById('navbar')
      if (!navbar) return
      
      window.addEventListener('scroll', () => {
        if (window.scrollY > 50) {
          navbar.classList.add('glass')
        } else {
          navbar.classList.remove('glass')
        }
      })
      
      document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.addEventListener('click', function (e) {
          e.preventDefault()
          const target = document.querySelector(this.getAttribute('href'))
          if (target) {
            target.scrollIntoView({ behavior: 'smooth' })
          }
        })
      })
    },
    initFullPageScroll() {
      this.handleWheel = this.handleWheel.bind(this)
      window.addEventListener('wheel', this.handleWheel, { passive: false })
    },
    handleWheel(e) {
      if (this.isScrolling) return
      
      const sections = document.querySelectorAll('section.fullpage-section')
      if (sections.length === 0) return
      
      e.preventDefault()
      const delta = e.deltaY
      
      if (delta > 0 && this.currentSection < sections.length - 1) {
        this.scrollToSection(this.currentSection + 1)
      } else if (delta < 0 && this.currentSection > 0) {
        this.scrollToSection(this.currentSection - 1)
      }
    },
    scrollToSection(index) {
      this.isScrolling = true
      this.currentSection = index
      
      const sections = document.querySelectorAll('section.fullpage-section')
      if (sections[index]) {
        sections[index].scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        })
      }
      
      setTimeout(() => {
        this.isScrolling = false
      }, 800)
    }
  }
}
</script>

<style scoped>
.home-container {
  min-height: 100vh;
  background: #020617;
}

.glass {
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.glass-card {
  background: rgba(30, 41, 59, 0.4);
  backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.glass-card:hover {
  background: rgba(30, 41, 59, 0.6);
  border-color: rgba(14, 165, 233, 0.3);
  transform: translateY(-8px);
  box-shadow: 0 20px 40px -10px rgba(14, 165, 233, 0.15);
}

.gradient-text {
  background: linear-gradient(135deg, #0ea5e9 0%, #6366f1 50%, #a855f7 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.gradient-text-gold {
  background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 50%, #fcd34d 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.grid-bg {
  background-image:
    linear-gradient(rgba(14, 165, 233, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(14, 165, 233, 0.03) 1px, transparent 1px);
  background-size: 50px 50px;
}

.donate-card {
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(251, 191, 36, 0.05) 100%);
  border: 1px solid rgba(245, 158, 11, 0.2);
}

.donate-card:hover {
  border-color: rgba(245, 158, 11, 0.4);
  box-shadow: 0 20px 40px -10px rgba(245, 158, 11, 0.15);
}

.nav-link {
  position: relative;
  overflow: hidden;
}

.nav-link::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 0;
  height: 2px;
  background: linear-gradient(90deg, #0ea5e9, #6366f1);
  transition: width 0.3s ease;
}

.nav-link:hover::after {
  width: 100%;
}

.btn-shine {
  position: relative;
  overflow: hidden;
}

.btn-shine::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s;
}

.btn-shine:hover::before {
  left: 100%;
}

.heart-beat {
  animation: heartbeat 1.5s ease-in-out infinite;
}

@keyframes heartbeat {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

.price-up {
  color: #10b981;
}

.price-down {
  color: #ef4444;
}

.ticker-wrap {
  overflow: hidden;
  white-space: nowrap;
}

.ticker {
  display: inline-block;
  animation: ticker 30s linear infinite;
}

@keyframes ticker {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
}

.reveal {
  opacity: 0;
  transform: translateY(50px);
  transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

.reveal.active {
  opacity: 1;
  transform: translateY(0);
}

.particles {
  position: absolute;
  width: 100%;
  height: 100%;
  overflow: hidden;
  pointer-events: none;
}

.particle {
  position: absolute;
  width: 4px;
  height: 4px;
  background: rgba(14, 165, 233, 0.5);
  border-radius: 50%;
  animation: particle-float 15s infinite;
}

@keyframes particle-float {
  0%, 100% {
    transform: translateY(100vh) rotate(0deg);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    transform: translateY(-100vh) rotate(720deg);
    opacity: 0;
  }
}

.float-1 {
  animation: float 6s ease-in-out infinite;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-20px);
  }
}

.mobile-menu {
  transform: translateX(100%);
  transition: transform 0.3s ease;
}

.mobile-menu.open {
  transform: translateX(0);
}

::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #0f172a;
}

::-webkit-scrollbar-thumb {
  background: #334155;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #475569;
}
</style>
